import React, { useMemo, useState, useEffect, useRef, useCallback } from 'react';
import {
  ScatterChart,
  Scatter,
  XAxis,
  YAxis,
  ZAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine
} from 'recharts';
import { ET_TIMEZONE } from '../utils/marketUtils';

// Helper function to calculate pairs from records using FIFO
const calculatePairsFromRecords = (records) => {
  const entryPrices = {};
  const exitPrices = {};

  records.forEach(record => {
    const symbol = record.symbol;
    if (record.action === 'entry') {
      if (!entryPrices[symbol]) entryPrices[symbol] = [];
      entryPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    } else if (record.action === 'exit') {
      if (!exitPrices[symbol]) exitPrices[symbol] = [];
      exitPrices[symbol].push({
        price: record.actualPrice,
        quantity: record.quantity,
        timestamp: record.timestamp
      });
    }
  });

  const pairs = [];
  Object.keys(entryPrices).forEach(symbol => {
    const entries = [...entryPrices[symbol]];
    const exits = exitPrices[symbol] || [];

    exits.forEach(exit => {
      if (entries.length > 0) {
        const entry = entries.shift();
        const qty = Math.min(entry.quantity, exit.quantity);
        pairs.push({
          symbol,
          quantity: qty,
          profit: (exit.price - entry.price) * qty,
          entryPrice: entry.price,
          exitPrice: exit.price,
          exit: { uiRecord: { timestamp: exit.timestamp } }
        });
      }
    });
  });

  return pairs;
};

// Get NY time from timestamp
const getNYTime = (timestamp) => {
  const date = new Date(timestamp);
  const nyHour = parseInt(date.toLocaleString('en-US', {
    timeZone: ET_TIMEZONE,
    hour: 'numeric',
    hour12: false
  }));
  const nyMinute = parseInt(date.toLocaleString('en-US', {
    timeZone: ET_TIMEZONE,
    minute: 'numeric'
  }));
  return { hour: nyHour, minute: nyMinute };
};

// Format exact time for display (minute precision)
const formatExactTime = (hour, minute) => {
  const h = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
  const ampm = hour >= 12 ? 'PM' : 'AM';
  return `${h}:${minute.toString().padStart(2, '0')}${ampm}`;
};

// Time range configurations
const TIME_RANGES = {
  market: { start: 9.5, end: 16, label: 'Market', description: '9:30AM-4PM' },
  extended: { start: 4, end: 20, label: 'Extended', description: '4AM-8PM' }
};

// Zoom level configurations (in hours) - 5 levels
const ZOOM_LEVELS = [
  { level: 1, window: null, label: 'Full', step: null },      // Full day view
  { level: 2, window: 4, label: '4h', step: 1 },              // 4 hour window
  { level: 3, window: 2, label: '2h', step: 0.5 },            // 2 hour window
  { level: 4, window: 1, label: '1h', step: 0.25 },           // 1 hour window
  { level: 5, window: 0.25, label: '15m', step: 0.05 }        // 15 min window
];

// Format time for display based on zoom level
const formatZoomTime = (time, zoomLevel) => {
  const hours = Math.floor(time);
  const minutes = Math.round((time - hours) * 60);
  const h = hours > 12 ? hours - 12 : (hours === 0 ? 12 : hours);
  const ampm = hours >= 12 ? 'PM' : 'AM';

  if (zoomLevel >= 6) {
    // Show minutes for fine zoom
    return `${h}:${minutes.toString().padStart(2, '0')}${ampm}`;
  } else {
    // Show just hour for coarse zoom
    return minutes === 0 ? `${h}${ampm}` : `${h}:${minutes.toString().padStart(2, '0')}`;
  }
};

/**
 * Time of Day Chart
 * Scatter view showing P&L by exit time
 * Two time ranges: Market (9:30AM-4PM) and Extended (4AM-8PM)
 */
const TimeOfDayChart = ({ records, pairs = [] }) => {
  const [timeRange, setTimeRange] = useState('market'); // 'market' | 'extended'
  const [dotScale, setDotScale] = useState(1); // 0.5 to 2.0 scale factor
  const [zoomLevel, setZoomLevel] = useState(1); // 1-7 zoom levels
  const [centerTime, setCenterTime] = useState(null); // Center time for zoomed view
  const [plFilter, setPlFilter] = useState('all'); // 'all' | 'profit' | 'loss'

  // Drag state for Google Maps-style panning
  const chartRef = useRef(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragStartX, setDragStartX] = useState(0);
  const [dragStartCenter, setDragStartCenter] = useState(null);

  // Get current view window based on zoom level
  const getViewWindow = useCallback(() => {
    const baseRange = TIME_RANGES[timeRange];
    const zoomConfig = ZOOM_LEVELS[zoomLevel - 1];

    if (zoomLevel === 1 || !zoomConfig.window) {
      return { start: baseRange.start, end: baseRange.end, window: baseRange.end - baseRange.start };
    }

    const center = centerTime || (baseRange.start + baseRange.end) / 2;
    const halfWindow = zoomConfig.window / 2;
    let start = Math.max(baseRange.start, center - halfWindow);
    let end = Math.min(baseRange.end, center + halfWindow);

    // Adjust if we hit boundaries
    if (start === baseRange.start) {
      end = Math.min(baseRange.end, start + zoomConfig.window);
    } else if (end === baseRange.end) {
      start = Math.max(baseRange.start, end - zoomConfig.window);
    }

    return { start, end, window: zoomConfig.window };
  }, [timeRange, zoomLevel, centerTime]);

  // Handle mouse wheel zoom (Google Maps style)
  const handleWheel = useCallback((e) => {
    e.preventDefault();
    const baseRange = TIME_RANGES[timeRange];

    // Determine zoom direction
    const zoomIn = e.deltaY < 0;

    if (zoomIn && zoomLevel < 5) {
      const newLevel = zoomLevel + 1;
      setZoomLevel(newLevel);

      // Calculate where the mouse is pointing (as a percentage across the chart)
      if (chartRef.current) {
        const rect = chartRef.current.getBoundingClientRect();
        const mouseX = e.clientX - rect.left;
        const chartWidth = rect.width - 50; // Account for margins
        const mousePercent = Math.max(0, Math.min(1, (mouseX - 20) / chartWidth));

        // Get current view and calculate new center based on mouse position
        const view = getViewWindow();
        const mouseTime = view.start + mousePercent * (view.end - view.start);

        // Set new center closer to mouse position
        const newWindow = ZOOM_LEVELS[newLevel - 1].window || (baseRange.end - baseRange.start);
        const newCenter = Math.max(
          baseRange.start + newWindow / 2,
          Math.min(baseRange.end - newWindow / 2, mouseTime)
        );
        setCenterTime(newCenter);
      } else if (centerTime === null) {
        setCenterTime((baseRange.start + baseRange.end) / 2);
      }
    } else if (!zoomIn && zoomLevel > 1) {
      setZoomLevel(zoomLevel - 1);
      if (zoomLevel - 1 === 1) {
        setCenterTime(null);
      }
    }
  }, [timeRange, zoomLevel, centerTime, getViewWindow]);

  // Handle drag start
  const handleMouseDown = useCallback((e) => {
    if (zoomLevel > 1) {
      setIsDragging(true);
      setDragStartX(e.clientX);
      setDragStartCenter(centerTime || (TIME_RANGES[timeRange].start + TIME_RANGES[timeRange].end) / 2);
      e.preventDefault();
    }
  }, [zoomLevel, centerTime, timeRange]);

  // Handle drag move
  const handleMouseMove = useCallback((e) => {
    if (!isDragging || zoomLevel <= 1) return;

    const baseRange = TIME_RANGES[timeRange];
    const view = getViewWindow();

    // Calculate how much to pan based on drag distance
    if (chartRef.current) {
      const rect = chartRef.current.getBoundingClientRect();
      const chartWidth = rect.width - 70; // Account for margins
      const deltaX = e.clientX - dragStartX;

      // Convert pixel movement to time units
      const timePerPixel = (view.end - view.start) / chartWidth;
      const deltaTime = -deltaX * timePerPixel; // Negative because dragging right should show earlier time

      // Calculate new center with bounds checking
      const zoomConfig = ZOOM_LEVELS[zoomLevel - 1];
      const halfWindow = (zoomConfig.window || (baseRange.end - baseRange.start)) / 2;
      const newCenter = Math.max(
        baseRange.start + halfWindow,
        Math.min(baseRange.end - halfWindow, dragStartCenter + deltaTime)
      );

      setCenterTime(newCenter);
    }
  }, [isDragging, zoomLevel, timeRange, dragStartX, dragStartCenter, getViewWindow]);

  // Handle drag end
  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse up listener for drag end
  useEffect(() => {
    if (isDragging) {
      window.addEventListener('mouseup', handleMouseUp);
      window.addEventListener('mousemove', handleMouseMove);
      return () => {
        window.removeEventListener('mouseup', handleMouseUp);
        window.removeEventListener('mousemove', handleMouseMove);
      };
    }
  }, [isDragging, handleMouseUp, handleMouseMove]);

  // Reset centerTime when timeRange changes to avoid out-of-bounds issues
  useEffect(() => {
    if (zoomLevel > 1) {
      const range = TIME_RANGES[timeRange];
      setCenterTime((range.start + range.end) / 2);
    }
  }, [timeRange]);

  // Process pairs data
  const { scatterData, totals } = useMemo(() => {
    const pairsToUse = pairs && pairs.length > 0 ? pairs : calculatePairsFromRecords(records);

    if (pairsToUse.length === 0) {
      return { scatterData: [], totals: null };
    }

    // Scatter data: each point = one trade (collect ALL trades)
    const scatterData = [];
    let noTimestamp = 0;

    pairsToUse.forEach((pair, idx) => {
      const timestamp = pair.exit?.uiRecord?.timestamp;
      if (!timestamp) {
        noTimestamp++;
        return;
      }

      const nyTime = getNYTime(timestamp);
      const hour = nyTime.hour;
      const minute = nyTime.minute;

      // Calculate exact time value for scatter (e.g., 9.5 = 9:30, 9.75 = 9:45)
      const exactTimeValue = hour + (minute / 60);

      // Check if within market hours (9:30 AM - 4:00 PM)
      const isInMarketHours = exactTimeValue >= 9.5 && exactTimeValue <= 16;
      const isProfit = (pair.profit || 0) >= 0;
      const profitValue = pair.profit || 0;

      // Add ALL trades to scatter data
      scatterData.push({
        id: idx,
        timeSlot: exactTimeValue,
        profit: profitValue,
        absProfit: Math.abs(profitValue),
        symbol: pair.symbol,
        quantity: pair.quantity || 0,
        entryPrice: pair.entryPrice || pair.entry?.uiRecord?.actualPrice,
        exitPrice: pair.exitPrice || pair.exit?.uiRecord?.actualPrice,
        isProfit,
        isInMarketHours,
        timeLabel: formatExactTime(hour, minute)
      });
    });

    // Calculate totals
    const marketTrades = scatterData.filter(d => d.isInMarketHours);
    const extendedTrades = scatterData.filter(d => !d.isInMarketHours);
    const totalProfit = scatterData.reduce((sum, d) => sum + (d.profit > 0 ? d.profit : 0), 0);
    const totalLoss = scatterData.reduce((sum, d) => sum + (d.profit < 0 ? Math.abs(d.profit) : 0), 0);

    // Calculate P&L stats for filter buttons
    const profitTrades = scatterData.filter(d => d.profit > 0);
    const lossTrades = scatterData.filter(d => d.profit < 0);

    return {
      scatterData,
      totals: {
        totalTrades: scatterData.length,
        marketTrades: marketTrades.length,
        extendedTrades: extendedTrades.length,
        totalProfit,
        totalLoss,
        netPL: totalProfit - totalLoss,
        noTimestamp,
        profitCount: profitTrades.length,
        lossCount: lossTrades.length
      }
    };
  }, [records, pairs]);

  // Filter scatter data by P&L filter
  const filteredScatterData = useMemo(() => {
    if (plFilter === 'all') return scatterData;
    if (plFilter === 'profit') return scatterData.filter(d => d.profit > 0);
    if (plFilter === 'loss') return scatterData.filter(d => d.profit < 0);
    return scatterData;
  }, [scatterData, plFilter]);

  // Scatter tooltip
  const ScatterTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      if (!data || data.profit === undefined) return null;

      const profit = data.profit || 0;
      const isProfit = profit >= 0;

      return (
        <div style={{
          background: 'white',
          border: `2px solid ${isProfit ? '#22c55e' : '#ef4444'}`,
          borderRadius: '8px',
          padding: '12px',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          minWidth: '160px'
        }}>
          <div style={{ fontWeight: 'bold', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{
              width: '12px',
              height: '12px',
              borderRadius: '50%',
              background: isProfit ? '#22c55e' : '#ef4444'
            }}></span>
            <span>{data.symbol || '-'}</span>
            <span style={{ color: '#999', fontSize: '12px' }}>@ {data.timeLabel || '-'} ET</span>
          </div>
          <div style={{
            color: isProfit ? '#22c55e' : '#ef4444',
            fontWeight: 'bold',
            fontSize: '20px',
            marginBottom: '8px'
          }}>
            {isProfit ? '+' : ''}${profit.toFixed(2)}
          </div>
          <div style={{ fontSize: '13px', color: '#666' }}>
            {data.entryPrice != null && data.exitPrice != null && (
              <div>${data.entryPrice.toFixed(2)} → ${data.exitPrice.toFixed(2)}</div>
            )}
            <div>Quantity: {data.quantity || 0} shares</div>
          </div>
        </div>
      );
    }
    return null;
  };

  if (!totals || totals.totalTrades === 0) {
    return (
      <div style={{
        height: 400,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: '#666'
      }}>
        No completed trades to display. Time of Day chart requires matched Entry/Exit pairs.
      </div>
    );
  }

  return (
    <div style={{ width: '100%' }}>
      {/* Time Range Toggle + Summary */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '16px',
        padding: '12px',
        background: '#f8f9fa',
        borderRadius: '8px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        {/* Time Range Toggle */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>Hours:</span>
          <button
            onClick={() => setTimeRange('market')}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: 'none',
              background: timeRange === 'market' ? '#10b981' : '#e5e7eb',
              color: timeRange === 'market' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '13px'
            }}
          >
            Market
            <span style={{ fontSize: '10px', opacity: 0.8, marginLeft: '4px' }}>9:30-4</span>
          </button>
          <button
            onClick={() => setTimeRange('extended')}
            style={{
              padding: '6px 12px',
              borderRadius: '6px',
              border: 'none',
              background: timeRange === 'extended' ? '#10b981' : '#e5e7eb',
              color: timeRange === 'extended' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '13px'
            }}
          >
            Extended
            <span style={{ fontSize: '10px', opacity: 0.8, marginLeft: '4px' }}>4AM-8PM</span>
          </button>
        </div>

        {/* P&L Filter */}
        <div style={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#666', marginRight: '4px' }}>Filter:</span>
          <button
            onClick={() => setPlFilter('all')}
            style={{
              padding: '5px 10px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'all' ? '#6366f1' : '#e5e7eb',
              color: plFilter === 'all' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            All ({totals?.totalTrades || 0})
          </button>
          <button
            onClick={() => setPlFilter('profit')}
            style={{
              padding: '5px 10px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'profit' ? '#22c55e' : '#e5e7eb',
              color: plFilter === 'profit' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            Profit ({totals?.profitCount || 0})
          </button>
          <button
            onClick={() => setPlFilter('loss')}
            style={{
              padding: '5px 10px',
              borderRadius: '6px',
              border: 'none',
              background: plFilter === 'loss' ? '#ef4444' : '#e5e7eb',
              color: plFilter === 'loss' ? 'white' : '#666',
              cursor: 'pointer',
              fontWeight: 500,
              fontSize: '12px'
            }}
          >
            Loss ({totals?.lossCount || 0})
          </button>
        </div>

        {/* Size Slider */}
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <span style={{ fontSize: '12px', color: '#666' }}>Size:</span>
          <span style={{ fontSize: '11px', color: '#999' }}>A</span>
          <input
            type="range"
            min="0.5"
            max="2"
            step="0.1"
            value={dotScale}
            onChange={(e) => setDotScale(parseFloat(e.target.value))}
            style={{
              width: '80px',
              cursor: 'pointer',
              accentColor: '#6366f1'
            }}
          />
          <span style={{ fontSize: '14px', color: '#999', fontWeight: 'bold' }}>A</span>
        </div>

        {/* Zoom Level Indicator - click to reset */}
        {zoomLevel > 1 && (
          <div
            onClick={() => {
              setZoomLevel(1);
              setCenterTime(null);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              padding: '4px 10px',
              background: '#6366f1',
              color: 'white',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: 500,
              cursor: 'pointer'
            }}
            title="Click to reset zoom"
          >
            <span>🔍 {ZOOM_LEVELS[zoomLevel - 1].label}</span>
            <span style={{ opacity: 0.8 }}>✕</span>
          </div>
        )}

        {/* Summary Stats */}
        <div style={{ display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#666' }}>Trades</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold' }}>
              {timeRange === 'market' ? totals.marketTrades : totals.totalTrades}
              {timeRange === 'market' && totals.extendedTrades > 0 && (
                <span style={{ fontSize: '11px', color: '#f59e0b', fontWeight: 'normal', marginLeft: '4px' }}>
                  +{totals.extendedTrades}
                </span>
              )}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#666' }}>Profit</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#22c55e' }}>
              +${totals.totalProfit.toFixed(0)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#666' }}>Loss</div>
            <div style={{ fontSize: '18px', fontWeight: 'bold', color: '#ef4444' }}>
              -${totals.totalLoss.toFixed(0)}
            </div>
          </div>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '11px', color: '#666' }}>Net P&L</div>
            <div style={{
              fontSize: '18px',
              fontWeight: 'bold',
              color: totals.netPL >= 0 ? '#22c55e' : '#ef4444'
            }}>
              {totals.netPL >= 0 ? '+' : ''}${totals.netPL.toFixed(0)}
            </div>
          </div>
        </div>
      </div>

      {/* Info notice for Market mode when there are extended trades */}
      {timeRange === 'market' && totals.extendedTrades > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          padding: '6px 12px',
          marginBottom: '12px',
          background: '#fef3c7',
          borderRadius: '6px',
          fontSize: '12px',
          color: '#92400e'
        }}>
          <span>💡 {totals.extendedTrades} trades outside market hours.</span>
          <button
            onClick={() => setTimeRange('extended')}
            style={{
              padding: '2px 8px',
              borderRadius: '4px',
              border: 'none',
              background: '#f59e0b',
              color: 'white',
              cursor: 'pointer',
              fontSize: '11px',
              fontWeight: 500
            }}
          >
            Show Extended
          </button>
        </div>
      )}

      {/* Chart Area - supports wheel zoom and drag pan */}
      <div
        ref={chartRef}
        onWheel={handleWheel}
        onMouseDown={handleMouseDown}
        style={{
          height: 380,
          width: '100%',
          cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
          userSelect: 'none'
        }}
      >
        <ResponsiveContainer>
          {(() => {
            const baseRange = TIME_RANGES[timeRange];
            const zoomConfig = ZOOM_LEVELS[zoomLevel - 1];

            // Calculate domain based on zoom level
            let domainStart, domainEnd;
            if (zoomLevel === 1 || !zoomConfig.window) {
              // Full view
              domainStart = baseRange.start;
              domainEnd = baseRange.end;
            } else {
              // Zoomed view - use centerTime
              const center = centerTime || (baseRange.start + baseRange.end) / 2;
              const halfWindow = zoomConfig.window / 2;
              domainStart = Math.max(baseRange.start, center - halfWindow);
              domainEnd = Math.min(baseRange.end, center + halfWindow);
              // Adjust if we hit boundaries
              if (domainStart === baseRange.start) {
                domainEnd = Math.min(baseRange.end, domainStart + zoomConfig.window);
              } else if (domainEnd === baseRange.end) {
                domainStart = Math.max(baseRange.start, domainEnd - zoomConfig.window);
              }
            }

            // Filter data for time range mode, P&L filter, and visible window
            let filteredData = timeRange === 'market'
              ? filteredScatterData.filter(d => d.isInMarketHours)
              : filteredScatterData;

            // Further filter to visible window when zoomed
            if (zoomLevel > 1) {
              filteredData = filteredData.filter(d =>
                d.timeSlot >= domainStart && d.timeSlot <= domainEnd
              );
            }

            // Generate ticks - evenly distributed across visible window
            const ticks = [];
            const windowSize = domainEnd - domainStart;

            if (zoomLevel === 1) {
              // Full view: hourly ticks
              for (let h = Math.ceil(baseRange.start); h <= baseRange.end; h++) {
                ticks.push(h);
              }
            } else {
              // Zoomed view: 5 evenly distributed ticks across visible window
              const tickCount = 5;
              const tickInterval = windowSize / (tickCount - 1);
              for (let i = 0; i < tickCount; i++) {
                ticks.push(domainStart + i * tickInterval);
              }
            }

            return (
              <ScatterChart margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  type="number"
                  dataKey="timeSlot"
                  name="Time"
                  domain={[domainStart, domainEnd]}
                  ticks={ticks}
                  tickFormatter={(v) => {
                    const hour = Math.floor(v);
                    const min = Math.round((v - hour) * 60);
                    const h = hour > 12 ? hour - 12 : (hour === 0 ? 12 : hour);
                    const ampm = hour >= 12 ? 'PM' : 'AM';

                    if (zoomLevel === 1) {
                      // Full view: show just hours
                      return `${h}${ampm}`;
                    } else {
                      // Zoomed view: always show hour:minute format
                      return `${h}:${min.toString().padStart(2, '0')}`;
                    }
                  }}
                  label={{ value: `Exit Time (ET) ${formatZoomTime(domainStart, zoomLevel)} - ${formatZoomTime(domainEnd, zoomLevel)}`, position: 'bottom', offset: 0 }}
                />
                <YAxis
                  type="number"
                  dataKey="profit"
                  name="P&L"
                  tickFormatter={(v) => `$${v}`}
                  label={{ value: 'P&L ($)', angle: -90, position: 'insideLeft' }}
                />
                <ZAxis type="number" dataKey="absProfit" range={[50, 400]} name="P&L Amount" />
                <Tooltip content={<ScatterTooltip />} />
                <ReferenceLine y={0} stroke="#666" strokeDasharray="3 3" />
                {/* Market hours reference lines in extended view - only show if visible */}
                {timeRange === 'extended' && 9.5 >= domainStart && 9.5 <= domainEnd && (
                  <ReferenceLine x={9.5} stroke="#10b981" strokeDasharray="5 5" strokeWidth={1} />
                )}
                {timeRange === 'extended' && 16 >= domainStart && 16 <= domainEnd && (
                  <ReferenceLine x={16} stroke="#10b981" strokeDasharray="5 5" strokeWidth={1} />
                )}
                <Scatter
                  name="Trades"
                  data={filteredData}
                  shape={(props) => {
                    const { cx, cy, payload } = props;
                    if (!cx || !cy || isNaN(cx) || isNaN(cy)) return null;

                    const color = payload.isProfit ? '#22c55e' : '#ef4444';
                    // Base size range: 6-20, scaled by dotScale (0.5-2.0)
                    const baseSize = Math.min(Math.max(6, Math.sqrt(payload.absProfit) / 3), 20);
                    const size = baseSize * dotScale;
                    const fontSize = Math.round(10 * dotScale);
                    const opacity = timeRange === 'extended' && !payload.isInMarketHours ? 0.5 : 0.8;

                    return (
                      <g key={`dot-${payload.id}`}>
                        <circle
                          cx={cx}
                          cy={cy}
                          r={size}
                          fill={color}
                          opacity={opacity}
                          style={{ cursor: 'pointer' }}
                        />
                        <text
                          x={cx}
                          y={cy - size - 4}
                          textAnchor="middle"
                          fill={color}
                          fontSize={fontSize}
                          fontWeight="bold"
                          opacity={opacity}
                        >
                          {payload.symbol}
                        </text>
                      </g>
                    );
                  }}
                />
              </ScatterChart>
            );
          })()}
        </ResponsiveContainer>
      </div>

      {/* Legend */}
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '20px',
        marginTop: '12px',
        fontSize: '13px',
        color: '#666',
        flexWrap: 'wrap'
      }}>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#22c55e' }}></span>
          Profit
        </span>
        <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
          <span style={{ width: '12px', height: '12px', borderRadius: '50%', background: '#ef4444' }}></span>
          Loss
        </span>
        <span>Dot size = P&L amount</span>
        {timeRange === 'extended' && (
          <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <span style={{ width: '1px', height: '12px', background: '#10b981', borderLeft: '2px dashed #10b981' }}></span>
            Market hours (9:30-4)
          </span>
        )}
        <span style={{ color: '#6366f1' }}>
          🖱️ Scroll to zoom {zoomLevel > 1 ? '| Drag to pan' : ''}
        </span>
      </div>
    </div>
  );
};

export default TimeOfDayChart;
